import { supabase } from "./supabase";
import { TaskAttachment } from "../types";

const BUCKET_NAME = "task-images";
const ATTACHMENTS_BUCKET = "task-attachments";

// Max 10 attachments per task
export const MAX_ATTACHMENTS_PER_TASK = 10;
// Max 10MB per file
export const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;

export const ALLOWED_ATTACHMENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",
];

/**
 * Upload an image to Supabase storage for use in task descriptions.
 * Images are stored in user-specific folders: {user_id}/{filename}
 *
 * @param file - The image file to upload
 * @param userId - The authenticated user's ID
 * @returns The public URL of the uploaded image
 * @throws Error if upload fails
 */
export async function uploadTaskImage(
  file: File,
  userId: string
): Promise<string> {
  // Generate unique filename with timestamp to avoid collisions
  const timestamp = Date.now();
  const extension = file.name.split(".").pop() || "png";
  const filename = `${timestamp}.${extension}`;
  const path = `${userId}/${filename}`;

  const { error } = await supabase.storage
    .from(BUCKET_NAME)
    .upload(path, file, {
      cacheControl: "3600",
      upsert: false,
    });

  if (error) {
    throw new Error(`Failed to upload image: ${error.message}`);
  }

  // Get public URL
  const {
    data: { publicUrl },
  } = supabase.storage.from(BUCKET_NAME).getPublicUrl(path);

  return publicUrl;
}

/**
 * Upload an attachment to Supabase storage.
 *
 * @param file - The file to upload
 * @param userId - The authenticated user's ID
 * @param taskId - The task ID this attachment belongs to
 * @returns The file path, storage URL, and metadata
 * @throws Error if upload fails or file is invalid
 */
export async function uploadTaskAttachment(
  file: File,
  userId: string,
  taskId: string
): Promise<{ file_path: string; storage_url: string; file_name: string; file_size: number; mime_type: string }> {
  // Validate file type
  if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
    throw new Error(`File type ${file.type} is not supported`);
  }

  // Validate file size
  if (file.size > MAX_ATTACHMENT_SIZE) {
    throw new Error(`File size exceeds ${formatFileSize(MAX_ATTACHMENT_SIZE)} limit`);
  }

  // Generate unique filename with timestamp to avoid collisions
  const timestamp = Date.now();
  const safeFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
  const path = `${userId}/${taskId}/${timestamp}_${safeFileName}`;

  const { error } = await supabase.storage
    .from(ATTACHMENTS_BUCKET)
    .upload(path, file, {
      cacheControl: "3600",
      upsert: false,
    });

  if (error) {
    throw new Error(`Failed to upload attachment: ${error.message}`);
  }

  // Get public URL
  const {
    data: { publicUrl },
  } = supabase.storage.from(ATTACHMENTS_BUCKET).getPublicUrl(path);

  return {
    file_path: path,
    storage_url: publicUrl,
    file_name: file.name,
    file_size: file.size,
    mime_type: file.type,
  };
}

/**
 * Delete an attachment from Supabase storage.
 *
 * @param attachment - The attachment record to delete
 * @param userId - The authenticated user's ID (for verification)
 * @throws Error if deletion fails
 */
export async function deleteTaskAttachment(
  attachment: TaskAttachment,
  userId: string
): Promise<void> {
  // Verify the attachment belongs to the user
  if (!attachment.file_path.startsWith(userId)) {
    throw new Error("Cannot delete attachments belonging to other users");
  }

  const { error } = await supabase.storage
    .from(ATTACHMENTS_BUCKET)
    .remove([attachment.file_path]);

  if (error) {
    throw new Error(`Failed to delete attachment: ${error.message}`);
  }
}

/**
 * Get the Lucide icon name for a file type.
 */
export function getFileIcon(mimeType: string): string {
  if (mimeType.startsWith("image/")) return "Image";
  if (mimeType === "application/pdf") return "FileText";
  if (
    mimeType === "application/msword" ||
    mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ) {
    return "FileText";
  }
  if (
    mimeType === "application/vnd.ms-excel" ||
    mimeType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  ) {
    return "FileSpreadsheet";
  }
  if (mimeType === "text/csv") return "FileSpreadsheet";
  if (mimeType === "text/plain") return "FileText";
  return "File";
}

/**
 * Format bytes as a human-readable file size.
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Check if an attachment is a previewable image.
 */
export function isImageAttachment(mimeType: string): boolean {
  return mimeType.startsWith("image/");
}

/**
 * Check if an attachment is a previewable PDF. Strips any `;charset=...`-
 * style parameter first so a stored MIME type that isn't the bare literal
 * still matches, the same tolerance `isImageAttachment`'s prefix check gets
 * for free.
 */
export function isPdfAttachment(mimeType: string): boolean {
  return mimeType.split(";")[0].trim() === "application/pdf";
}

/**
 * Force-download a file from a cross-origin URL (e.g. Supabase Storage's
 * public URL) as `fileName`. The `download` attribute on an `<a>` is ignored
 * by browsers once `href` and the page's origin differ, so a plain anchor
 * would just navigate to the file instead of saving it — fetch the bytes
 * ourselves and save from a same-origin blob URL instead.
 */
export async function downloadAttachmentFile(
  url: string,
  fileName: string
): Promise<void> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download ${fileName}: ${response.status}`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 150);
}
